import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import UserIcon from "@redotech/redo-icons/user-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { GetUser } from "@redotech/redo-model/user";
import { RedoAvatar } from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo, type MouseEvent } from "react";
import type { ConversationPresenceRecord } from "../db/collections";
import { useSupportTicketCard } from "../hooks/use-support-ticket-card";
import { useSupportTicketPresence } from "../hooks/use-support-ticket-data";
import { PriorityBadge, StatusBadge } from "./badges/support-ticket-badges";
import * as styles from "./support-ticket-card.module.css";

export interface SupportTicketCardProps {
  conversation: SupportTicketListItemResponse;
  isActive: boolean;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
}

export const SupportTicketCard = memo(function SupportTicketCard({
  conversation,
  isActive,
  onClick,
}: SupportTicketCardProps) {
  const { isUnread, timeSinceMessage, lastMessageDate } = useSupportTicketCard({
    conversation,
  });
  const { data: presenceUsers } = useSupportTicketPresence(
    conversation.id as SupportTicketId,
  );
  const team = useRequiredContext(TeamContext);
  const userMap = useMemo(
    () =>
      new Map(
        team.users.map((u) => {
          const user = u.user as GetUser;
          return [user._id, user.name];
        }),
      ),
    [team.users],
  );

  return (
    <Flex
      bgColor={isActive ? "quaternary" : "primary"}
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      className={styles.card}
      cursor="pointer"
      hoverColor={isActive ? undefined : "primary"}
      onClick={onClick}
      p="lg"
    >
      <Flex dir="column" gap="sm" overflow="hidden" w="full">
        {/* Row 1: Header - Name/time column + unread dot */}
        <Flex align="flex-start" justify="space-between">
          <Flex dir="column" gap="none">
            <Text
              fontSize="xs"
              fontWeight={isUnread ? "semibold" : "regular"}
              textColor="primary"
            >
              {conversation.customer?.displayName ?? "Customer"}
            </Text>
            {lastMessageDate && (
              <Tooltip arrow title={lastMessageDate.toLocaleString()}>
                <span>
                  <Text fontSize="xxs" textColor="tertiary">
                    {timeSinceMessage}
                  </Text>
                </span>
              </Tooltip>
            )}
          </Flex>
          {isUnread && (
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "black",
              }}
            />
          )}
        </Flex>

        {/* Row 2: Subject + Preview */}
        <Flex dir="column" gap="none">
          {conversation.summary && (
            <Text className={styles.summary} fontSize="xs" textColor="tertiary">
              {conversation.summary}
            </Text>
          )}
        </Flex>

        {/* Row 3: Badges */}
        <Flex align="center" gap="xs">
          <StatusBadge size="sm" status={conversation.status} />
          {conversation.priority === "URGENT" && (
            <PriorityBadge priority={conversation.priority} size="sm" />
          )}
        </Flex>

        {/* Row 4: Presence avatars */}
        {presenceUsers.length > 0 && (
          <PresenceAvatarGroup userMap={userMap} users={presenceUsers} />
        )}
      </Flex>
    </Flex>
  );
});

const MAX_VISIBLE_AVATARS = 3;

const PresenceAvatarGroup = memo(function PresenceAvatarGroup({
  users,
  userMap,
}: {
  users: ConversationPresenceRecord[];
  userMap: Map<string, string>;
}) {
  const overlap = 8;
  const visible = users.slice(0, MAX_VISIBLE_AVATARS);
  const overflow = users.length - visible.length;

  return (
    <Flex align="center">
      <Flex position="relative">
        {visible.map((user, index) => {
          const name = userMap.get(user.userId);
          const label = user.isTyping
            ? `${name ?? user.userId} is typing…`
            : `${name ?? user.userId} is viewing`;

          return (
            <Tooltip arrow key={user.id} title={label}>
              <div
                style={{
                  marginLeft: index === 0 ? 0 : -overlap,
                  position: "relative",
                  zIndex: MAX_VISIBLE_AVATARS - index,
                }}
              >
                <RedoAvatar
                  content={
                    name
                      ? { type: "text", text: name }
                      : { type: "icon", Icon: UserIcon }
                  }
                  size="xs"
                  status={
                    user.isTyping
                      ? { type: "typing" }
                      : { type: "presence", color: "green" }
                  }
                />
              </div>
            </Tooltip>
          );
        })}
      </Flex>
      {overflow > 0 && (
        <Text fontSize="xxs" ml="xs" textColor="tertiary">
          +{overflow}
        </Text>
      )}
    </Flex>
  );
});

export interface SupportTicketCardSkeletonProps {
  count?: number;
}

export const SupportTicketCardSkeleton = memo(
  function SupportTicketCardSkeleton({
    count = 3,
  }: SupportTicketCardSkeletonProps) {
    return (
      <>
        {Array.from({ length: count }).map((_, index) => (
          <Flex
            borderBottomWidth="1px"
            borderColor="secondary"
            borderStyle="solid"
            key={index}
            p="lg"
          >
            <Flex dir="column" gap="sm">
              <Flex align="center" gap="sm" justify="space-between">
                <div
                  style={{
                    width: 100,
                    height: 14,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 4,
                  }}
                />
                <div
                  style={{
                    width: 40,
                    height: 12,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 4,
                  }}
                />
              </Flex>
              <div
                style={{
                  width: "100%",
                  height: 28,
                  backgroundColor: "var(--redo-colors-background-bg-tertiary)",
                  borderRadius: 4,
                }}
              />
              <Flex align="center" gap="xs">
                <div
                  style={{
                    width: 50,
                    height: 20,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 10,
                  }}
                />
              </Flex>
            </Flex>
          </Flex>
        ))}
      </>
    );
  },
);

export const SupportTicketCardEmpty = memo(function SupportTicketCardEmpty() {
  return (
    <Flex align="center" justify="center" p="xl">
      <Text fontSize="xs" textColor="tertiary">
        No conversations
      </Text>
    </Flex>
  );
});
